import { useState, useEffect, useCallback } from 'react';
import createContextHook from '@nkzw/create-context-hook';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useDogs } from '@/context/DogsContext';

const STORAGE_KEY = 'selectedDogId';

/**
 * The dog chosen for trail matching (`?dogId=`). Persisted to AsyncStorage,
 * defaults to undefined (= first dog / backend default). Clamped to a dog
 * that still exists in DogsContext when the list changes.
 */
export const [SelectedDogContext, useSelectedDog] = createContextHook(() => {
  const { dogProfiles } = useDogs();
  const [dogId, setDogIdState] = useState<string | undefined>(undefined);

  // Load persisted selection on mount.
  useEffect(() => {
    AsyncStorage.getItem(STORAGE_KEY).then((stored) => {
      if (stored) setDogIdState(stored);
    }).catch(() => {});
  }, []);

  // Clamp: if the stored dog no longer exists, drop it.
  useEffect(() => {
    if (dogId && dogProfiles.length > 0 && !dogProfiles.some((d) => d.id === dogId)) {
      setDogIdState(undefined);
      AsyncStorage.removeItem(STORAGE_KEY).catch(() => {});
    }
  }, [dogId, dogProfiles]);

  const setDogId = useCallback((id: string | undefined) => {
    setDogIdState(id);
    if (id) AsyncStorage.setItem(STORAGE_KEY, id).catch(() => {});
    else AsyncStorage.removeItem(STORAGE_KEY).catch(() => {});
  }, []);

  return { dogId, setDogId };
});